import { ThemeEvents, VariantUpdateEvent } from '@theme/events';

/**
 * Notify Me! (notify-me.io) PreOrder workarounds.
 *
 * Price format: on a pre-order variant the app hides the theme's price with an
 * inline `display: none !important` and renders its own element, formatting the
 * amount with a hardcoded `toFixed(2)` — "£3800.00" where the shop money format
 * (`£{{amount_no_decimals}}`) is "£3,800". Every app price span carries
 * `hengam-price` plus a context class (hengam-final-price /
 * hengam-pre-discount-price on the product page; hengam-preorder-final-price and
 * hengam-preorder-pre-discount-price on product cards, the featured product and
 * the pre-order landing page), so format all of them in the store's format.
 *
 * App copy while the apps are being trialled: the "Full payment" selling-plan
 * card, its duplicate description note and the low-stock line are hidden. CSS
 * only, so the app's selling-plan radio stays checked and still submits. Un-hide
 * `.hengam-preorder-selling-plan-selector` if a partial-payment/deposit option is
 * ever enabled — otherwise customers lose the choice.
 */
const APP_PRICE_SELECTOR = '.hengam-price';
const HIDDEN_APP_SELECTORS = [
  '.hengam-preorder-selling-plan-selector',
  '.hengam-preorder-description',
  '.hengam-low-stock-description',
];

function formatAmountWithoutDecimals(text) {
  const parts = text.match(/^([^\d]*)([\d.,]+)(.*)$/s);
  if (!parts) return text;

  const [, prefix, rawAmount, suffix] = parts;
  const amount = Number(rawAmount.replace(/,/g, ''));
  if (!Number.isFinite(amount)) return text;

  const formatted = new Intl.NumberFormat(document.documentElement.lang || 'en', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);

  return `${prefix}${formatted}${suffix}`;
}

function formatAppPrices() {
  for (const element of document.querySelectorAll(APP_PRICE_SELECTOR)) {
    if (element.children.length) continue; // price spans only, never their wrappers
    const formatted = formatAmountWithoutDecimals(element.textContent.trim());
    if (formatted !== element.textContent) element.textContent = formatted;
  }
}

/**
 * Runs only when a mutation actually touches an app price, so page-wide
 * observation stays off the hot path.
 */
function mutationTouchesAppPrice(mutations) {
  return mutations.some((mutation) => {
    if (mutation.type === 'characterData') {
      return Boolean(mutation.target.parentElement?.closest(APP_PRICE_SELECTOR));
    }
    return [...mutation.addedNodes].some(
      (node) =>
        node.nodeType === Node.ELEMENT_NODE &&
        (node.matches?.(APP_PRICE_SELECTOR) || node.querySelector?.(APP_PRICE_SELECTOR))
    );
  });
}

let appPriceObserver = null;

/**
 * Hides the app copy listed above and keeps every app price in the shop money
 * format. Runs once per page: the app injects into product pages, cards, the
 * featured product, the pre-order landing page and quick-add dialogs.
 */
function observeAppPrices() {
  if (appPriceObserver || !document.body) return;

  document.head.insertAdjacentHTML(
    'beforeend',
    `<style>${HIDDEN_APP_SELECTORS.join(',')} { display: none !important; }</style>`
  );

  formatAppPrices();

  let pending = false;
  appPriceObserver = new MutationObserver((mutations) => {
    if (!mutationTouchesAppPrice(mutations) || pending) return;
    pending = true;
    requestAnimationFrame(() => {
      pending = false;
      formatAppPrices();
    });
  });
  appPriceObserver.observe(document.body, { childList: true, subtree: true, characterData: true });
}

observeAppPrices();

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 */
class ProductPrice extends HTMLElement {
  connectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updatePrice);
  }

  disconnectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(ThemeEvents.variantUpdate, this.updatePrice);
  }

  /**
   * Updates the price.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const newPrice = event.detail.data.html.querySelector('product-price [ref="priceContainer"]');
    const currentPrice = this.querySelector('[ref="priceContainer"]');

    if (!newPrice || !currentPrice) return;

    if (currentPrice.innerHTML !== newPrice.innerHTML) {
      currentPrice.replaceWith(newPrice);
    }
  };
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}
